import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Loading from '../components/loading';
import { useAppointmentHistory } from './hooks/useAppointmentHistory';

// Mesmas cores dos badges de status da tela da consulta.
const STATUS_COLORS = {
  SCHEDULED: 'info',
  CONFIRMED: 'primary',
  CONFIRMED_BY_PATIENT: 'primary',
  PERFORMED: 'success',
  CANCELED: 'error',
  NO_SHOW: 'warning',
  REARRANGED: 'secondary',
};

const PATIENT_APP_USER = 'patient-app';

function authorLabel(createdBy) {
  return createdBy === PATIENT_APP_USER ? 'Paciente (aplicativo)' : createdBy;
}

export default function AppointmentHistoryModal({ open, onClose, appointmentId }) {
  const { history, isLoading, error } = useAppointmentHistory(open ? appointmentId : null);

  return (
    <Dialog
      data-cy="appointment-history-modal"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { minHeight: '300px' } } }}
    >
      <Loading isToHidden={isLoading === false} />
      <DialogTitle>
        <Typography component="span" sx={{ fontWeight: 'bold' }}>
          Histórico da Consulta
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error">{error}</Alert>}
        {!error && history.length > 0 && (
          <Timeline
            sx={{ mt: 1, p: 0, [`& .MuiTimelineItem-root::before`]: { flex: 0, padding: 0 } }}
          >
            {history.map((item, index) => (
              <TimelineItem key={item.id} data-cy="appointment-history-item">
                <TimelineSeparator>
                  <TimelineDot color={STATUS_COLORS[item.status] ?? 'grey'} />
                  {index < history.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent sx={{ py: '10px', px: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {dayjs(item.createdAt).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                    <Chip
                      size="small"
                      label={item.statusDescription}
                      color={STATUS_COLORS[item.status] ?? 'default'}
                      variant="outlined"
                    />
                  </Box>
                  {item.description && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {item.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    por {authorLabel(item.createdBy)}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            ))}
          </Timeline>
        )}
        {!error && !isLoading && history.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Nenhum histórico registrado para esta consulta.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

AppointmentHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  appointmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
